/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * White-Label / Brand infrastructure.
 *
 * ONE source of truth for aesthetics + business variables:
 *
 *   1. `public/brand-config.json`  — static file shipped with the build. The clinic
 *      edits it (logo, colours, price list, ISEE tiers) and reloads. No rebuild.
 *   2. `GET /api/brand-settings`   — dynamic overrides saved from the admin UI
 *      (Brand Settings page / Installer Wizard). Wins over the JSON file.
 *   3. `DEFAULT_BRAND`             — hard defaults when nothing else is present.
 *
 * Every colour is converted hex → HSL and written as CSS variables
 * (`--primary`, `--accent`, `--ring`, ...) so that the ENTIRE Tailwind theme
 * (buttons, badges, focus rings, charts) follows the brand in light AND dark
 * mode. See `brandTheme.js`.
 *
 * `REACT_APP_MODE=whitelabel` additionally switches paid AI to local engines
 * (see ai-mocks.js) and WebRTC to demo mode — that behaviour is unchanged.
 */
import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { applyBrandTheme } from '@/lib/brandTheme';
import { applyBrandIcons } from '@/lib/brandIcons';
import { THEME_CONFIG, themeConfigToBrand } from '@/config/themeConfig';

export const WHITELABEL_MODE = (process.env.REACT_APP_MODE || '').toLowerCase() === 'whitelabel';

export const isWhitelabel = () => WHITELABEL_MODE;

/**
 * Default brand values — derived from the SINGLE central config
 * `src/config/themeConfig.js`. Never duplicate colours/names here.
 */
export const DEFAULT_BRAND = themeConfigToBrand(THEME_CONFIG);

const BrandContext = createContext({ ...DEFAULT_BRAND, isWhitelabel: WHITELABEL_MODE, reload: () => {} });

// ---------------------------------------------------------------------------
// Tenant key (AI Usage Counter). Letta da api.js per l'header `x-clinic-id`.
// Precedenza: brand.clinicId (DB admin > brand-config.json > REACT_APP_CLINIC_ID)
//   > slug del nome clinica (solo white-label) > "default".
// ---------------------------------------------------------------------------
const slugify = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
const deriveClinicId = (brand) => {
  const explicit = String(brand?.clinicId || '').trim();
  if (/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/.test(explicit)) return explicit;
  if (WHITELABEL_MODE) return slugify(brand?.clinicName) || 'default';
  return 'default';
};
let _currentClinicId = deriveClinicId(readBrandCacheEarly());
export const getClinicId = () => _currentClinicId;

function readBrandCacheEarly() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('sn_brand_cache_v1') : null;
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

// ---------------------------------------------------------------------------
// Zero-latency first paint: cache the last applied brand (non-sensitive:
// colours, names, logo/favicon URL) so palette + logo are applied
// synchronously on reload, before the network round-trips complete.
// ---------------------------------------------------------------------------
const BRAND_CACHE_KEY = 'sn_brand_cache_v1';
const CACHEABLE_KEYS = ['clinicId', 'clinicName', 'clinicShortName', 'tagline', 'primaryColor', 'primaryColorAlt', 'secondaryColor', 'accentColor', 'darkPrimaryColor', 'logoUrl', 'faviconUrl'];

const readBrandCache = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(BRAND_CACHE_KEY) : null;
    const json = raw ? JSON.parse(raw) : null;
    return json && typeof json === 'object' ? json : null;
  } catch (e) { return null; }
};

const writeBrandCache = (brand) => {
  try {
    const slim = Object.fromEntries(CACHEABLE_KEYS.filter((k) => brand[k] !== undefined && brand[k] !== null).map((k) => [k, brand[k]]));
    // Logo data-URI can be large (~1MB): keep the cache small for a fast sync read.
    if (typeof slim.logoUrl === 'string' && slim.logoUrl.length > 200_000) delete slim.logoUrl;
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(slim));
  } catch (e) { /* quota / private mode: ignore */ }
};

/** Removes null/undefined so a sparse JSON file never wipes a default. */
const compact = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([k, v]) => v !== null && v !== undefined && !k.startsWith('$')),
);

/** Deep-merge only for the nested objects we know about (priceList, features). */
const mergeBrand = (base, overrides) => {
  const o = compact(overrides);
  return {
    ...base,
    ...o,
    priceList: { ...(base.priceList || {}), ...compact(o.priceList) },
    features: { ...(base.features || {}), ...compact(o.features) },
    // Keep legacy alias in sync
    primaryColorAlt: o.secondaryColor || o.primaryColorAlt || base.primaryColorAlt,
    secondaryColor: o.secondaryColor || o.primaryColorAlt || base.secondaryColor,
  };
};

const fetchJson = async (url, opts = {}) => {
  const r = await fetch(url, { cache: 'no-store', ...opts });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(() => {
    const cached = readBrandCache();
    return cached ? mergeBrand(DEFAULT_BRAND, cached) : DEFAULT_BRAND;
  });
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    let next = DEFAULT_BRAND;

    // 1) Static brand-config.json. Applied in whitelabel builds, or in the SaaS
    //    demo only when the file opts in with `"applyToSaaS": true` (so the
    //    public SanitàNexus demo keeps its own identity by default).
    for (const file of ['/brand-config.json', '/whitelabel.config.json']) {
      try {
        const json = await fetchJson(file);
        if (WHITELABEL_MODE || json?.applyToSaaS === true) next = mergeBrand(next, json);
        break;
      } catch (e) { /* try next */ }
    }

    // 2) Dynamic admin overrides from the API (DB). Wins over the file.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend) {
        const json = await fetchJson(`${backend}/api/brand-settings`, { credentials: 'include' });
        // In SaaS mode we only take colours/logo/name from the DB if an admin
        // explicitly saved them (the endpoint flags defaults with `is_default`).
        if (WHITELABEL_MODE || !json?.is_default) next = mergeBrand(next, json);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[brand] api brand-settings unavailable');
    }

    setBrand(next);
    setLoaded(true);
    writeBrandCache(next);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply the palette to the whole design system (light + dark) + document chrome.
  // useLayoutEffect: runs synchronously before the browser paints -> no flash of
  // default theme when a cached/updated brand is available.
  useLayoutEffect(() => {
    _currentClinicId = deriveClinicId(brand);
    if (typeof document === 'undefined') return;
    applyBrandTheme(brand);
    if (brand.clinicName) document.title = brand.clinicName;
    // Favicon, apple-touch-icon e manifest PWA coerenti con il brand (stessa geometria del marchio
    // in app; usa il logo caricato se presente). Con il brand di default restano i file statici.
    const isDefault = !brand.logoUrl && !brand.faviconUrl
      && (brand.primaryColor || '').toUpperCase() === (DEFAULT_BRAND.primaryColor || '').toUpperCase()
      && brand.clinicName === DEFAULT_BRAND.clinicName;
    applyBrandIcons(brand, { isDefault });
  }, [brand]);

  const value = useMemo(
    () => ({ ...brand, isWhitelabel: WHITELABEL_MODE, brandLoaded: loaded, reload: load }),
    [brand, loaded, load],
  );
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrand = () => useContext(BrandContext);

/** Convenience: formatted price using the brand currency. */
export const formatBrandPrice = (value, brand) => {
  const currency = brand?.priceList?.currency || 'EUR';
  try {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(Number(value) || 0);
  } catch (e) {
    return `${Number(value || 0).toFixed(2)} ${currency}`;
  }
};
